//# sourceMappingURL=decrypt.mjs.map
/// <reference types="./decrypt.d.mts" />
import * as $dynamic from "../gleam_stdlib/gleam/dynamic.mjs";
import * as $decode from "../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $float from "../gleam_stdlib/gleam/float.mjs";
import * as $int from "../gleam_stdlib/gleam/int.mjs";
import * as $list from "../gleam_stdlib/gleam/list.mjs";
import * as $option from "../gleam_stdlib/gleam/option.mjs";
import { None } from "../gleam_stdlib/gleam/option.mjs";
import * as $pair from "../gleam_stdlib/gleam/pair.mjs";
import * as $result from "../gleam_stdlib/gleam/result.mjs";
import * as $string from "../gleam_stdlib/gleam/string.mjs";
import * as $calendar from "../gleam_time/gleam/time/calendar.mjs";
import * as $timestamp from "../gleam_time/gleam/time/timestamp.mjs";
import * as $function from "../tiamat/tiamat/function.mjs";
import * as $uuid from "../youid/youid/uuid.mjs";
import { newDecoder as new_decoder, decodeJSON as decode_json } from "./decrypt.ffi.mjs";
import { Ok, toList } from "./gleam.mjs";

function float_string() {
  return $decode.then$(
    $decode.string,
    (string) => {
      let $ = $float.parse(string);
      if ($ instanceof Ok) {
        let value = $[0];
        return $decode.success(value);
      } else {
        return $decode.failure(0.0, "Float");
      }
    },
  );
}

function int_string() {
  return $decode.then$(
    $decode.string,
    (string) => {
      let $ = $int.parse(string);
      if ($ instanceof Ok) {
        let value = $[0];
        return $decode.success(value);
      } else {
        return $decode.failure(0, "Int");
      }
    },
  );
}

/**
 * Decode any number as in `Float`. Floats will be kept as is, while integers
 * numbers will be turned to float. Any number will be
 * converted, whether they're real numbers or numbers turned into strings.
 *
 * ```gleam
 * decode.run(dynamic.from(1), decrypt.float())     // -> Ok(1.0)
 * decode.run(dynamic.from(1.0), decrypt.float())   // -> Ok(1.0)
 * decode.run(dynamic.from(1.1), decrypt.float())   // -> Ok(1.1)
 * decode.run(dynamic.from(1.5), decrypt.float())   // -> Ok(1.5)
 * decode.run(dynamic.from("1"), decrypt.float())   // -> Ok(1.0)
 * decode.run(dynamic.from("1.0"), decrypt.float()) // -> Ok(1.0)
 * decode.run(dynamic.from("1.1"), decrypt.float()) // -> Ok(1.1)
 * decode.run(dynamic.from("1.5"), decrypt.float()) // -> Ok(1.5)
 * ```
 */
export function float() {
  let _block;
  let _pipe = $decode.int;
  _block = $decode.map(_pipe, $int.to_float);
  let as_float = _block;
  let _block$1;
  let _pipe$1 = int_string();
  _block$1 = $decode.map(_pipe$1, $int.to_float);
  let int_string$1 = _block$1;
  let float_string$1 = float_string();
  let _pipe$2 = $decode.one_of(
    $decode.float,
    toList([as_float, float_string$1, int_string$1]),
  );
  return $decode.collapse_errors(_pipe$2, "Float");
}

/**
 * Decode any number as in `Int`. Integers will be kept as is, while floating
 * numbers will be rounded to the nearest integer. Any number will be
 * converted, whether they're real numbers or numbers turned into strings.
 *
 * ```gleam
 * decode.run(dynamic.from(1), decrypt.int())     // -> Ok(1)
 * decode.run(dynamic.from(1.0), decrypt.int())   // -> Ok(1)
 * decode.run(dynamic.from(1.1), decrypt.int())   // -> Ok(1)
 * decode.run(dynamic.from(1.5), decrypt.int())   // -> Ok(2)
 * decode.run(dynamic.from("1"), decrypt.int())   // -> Ok(1)
 * decode.run(dynamic.from("1.0"), decrypt.int()) // -> Ok(1)
 * decode.run(dynamic.from("1.1"), decrypt.int()) // -> Ok(1)
 * decode.run(dynamic.from("1.5"), decrypt.int()) // -> Ok(2)
 * ```
 */
export function int() {
  let _block;
  let _pipe = $decode.float;
  _block = $decode.map(_pipe, $float.round);
  let as_int = _block;
  let int_string$1 = int_string();
  let _block$1;
  let _pipe$1 = float_string();
  _block$1 = $decode.map(_pipe$1, $float.round);
  let float_string$1 = _block$1;
  let _pipe$2 = $decode.one_of(
    $decode.int,
    toList([as_int, int_string$1, float_string$1]),
  );
  return $decode.collapse_errors(_pipe$2, "Int");
}

/**
 * Decode an integer in comprised in [1..12] to its corresponding month
 * in `gleam/time/calendar.Month`.
 *
 * ```gleam
 * decode.run(dynamic.from(1), decrypt.month())  // -> Ok(calendary.January)
 * decode.run(dynamic.from(2), decrypt.month())  // -> Ok(calendar.February)
 * decode.run(dynamic.from(3), decrypt.month())  // -> Ok(calendar.March)
 * decode.run(dynamic.from(4), decrypt.month())  // -> Ok(calendar.April)
 * decode.run(dynamic.from(5), decrypt.month())  // -> Ok(calendar.May)
 * decode.run(dynamic.from(6), decrypt.month())  // -> Ok(calendar.June)
 * decode.run(dynamic.from(7), decrypt.month())  // -> Ok(calendar.July)
 * decode.run(dynamic.from(8), decrypt.month())  // -> Ok(calendar.August)
 * decode.run(dynamic.from(9), decrypt.month())  // -> Ok(calendar.September)
 * decode.run(dynamic.from(10), decrypt.month()) // -> Ok(calendar.October)
 * decode.run(dynamic.from(11), decrypt.month()) // -> Ok(calendar.November)
 * decode.run(dynamic.from(12), decrypt.month()) // -> Ok(calendar.December)
 * ```
 */
export function month() {
  return $decode.then$(
    $decode.int,
    (month) => {
      if (month === 1) {
        return $decode.success(new $calendar.January());
      } else if (month === 2) {
        return $decode.success(new $calendar.February());
      } else if (month === 3) {
        return $decode.success(new $calendar.March());
      } else if (month === 4) {
        return $decode.success(new $calendar.April());
      } else if (month === 5) {
        return $decode.success(new $calendar.May());
      } else if (month === 6) {
        return $decode.success(new $calendar.June());
      } else if (month === 7) {
        return $decode.success(new $calendar.July());
      } else if (month === 8) {
        return $decode.success(new $calendar.August());
      } else if (month === 9) {
        return $decode.success(new $calendar.September());
      } else if (month === 10) {
        return $decode.success(new $calendar.October());
      } else if (month === 11) {
        return $decode.success(new $calendar.November());
      } else if (month === 12) {
        return $decode.success(new $calendar.December());
      } else {
        return $decode.failure(new $calendar.January(), "Month");
      }
    },
  );
}

function seconds_decoder() {
  let int$1 = $decode.map(
    $decode.int,
    (_capture) => { return $pair.new$(_capture, 0); },
  );
  let float$1 = $decode.map(
    $decode.float,
    (f) => {
      let floored = $float.floor(f);
      let seconds = $float.round(floored);
      let nanoseconds = $float.round((f - floored) * 1000000000.0);
      return [seconds, nanoseconds];
    },
  );
  return $decode.one_of(int$1, toList([float$1]));
}

/**
 * PostgreSQL stores timestamp with a precision of microseconds. When a
 * timestamp is read as int, parse it correctly.
 * 
 * @ignore
 */
function microseconds_decoder() {
  return $decode.map(
    $decode.int,
    (microseconds) => {
      let seconds = globalThis.Math.trunc(microseconds / 1_000_000);
      let microseconds$1 = microseconds % 1_000_000;
      let nanoseconds = microseconds$1 * 1000;
      return $timestamp.from_unix_seconds_and_nanoseconds(seconds, nanoseconds);
    },
  );
}

/**
 * Decode an RFC-3339 string into a `gleam/time/timestamp.Timestamp`.
 * RFC-3339 is often referred as ISO-8601, but is more strict than the ISO
 * format, whereas ISO format refers to many different (unused) formats.
 * 
 * @ignore
 */
function rfc3339_decoder() {
  return $decode.then$(
    $decode.string,
    (content) => {
      let $ = $timestamp.parse_rfc3339(content);
      if ($ instanceof Ok) {
        let time = $[0];
        return $decode.success(time);
      } else {
        return $decode.failure($timestamp.system_time(), "Timestamp");
      }
    },
  );
}

/**
 * Decode a tuple timestamp, following the tuple shape:
 * `{{year, month, day}, {hours, minutes, seconds.nanoseconds}}`.
 * `year >= 0`, `month == [1..12]`, `day == [1..31]`.
 * `hours == [0..23]`, `minutes == [0..59]`,
 * `seconds == [0..59].[0..1_000_000_000]`.
 * 
 * @ignore
 */
function tuple_timestamp() {
  return $decode.field(
    0,
    $decode.field(
      0,
      int(),
      (year) => {
        return $decode.field(
          1,
          month(),
          (month) => {
            return $decode.field(
              2,
              int(),
              (day) => {
                let _pipe = new $calendar.Date(year, month, day);
                return $decode.success(_pipe);
              },
            );
          },
        );
      },
    ),
    (date) => {
      return $decode.field(
        1,
        $decode.field(
          0,
          int(),
          (hours) => {
            return $decode.field(
              1,
              int(),
              (minutes) => {
                return $decode.field(
                  2,
                  seconds_decoder(),
                  (_use0) => {
                    let seconds = _use0[0];
                    let nanoseconds = _use0[1];
                    let _pipe = new $calendar.TimeOfDay(
                      hours,
                      minutes,
                      seconds,
                      nanoseconds,
                    );
                    return $decode.success(_pipe);
                  },
                );
              },
            );
          },
        ),
        (time) => {
          let _pipe = $timestamp.from_calendar(date, time, $calendar.utc_offset);
          return $decode.success(_pipe);
        },
      );
    },
  );
}

/**
 * Decode a `gleam.Timestamp`, whether it is an RFC-3339 string or an Erlang
 * tuple timestamp (`{{year, month, day}, {hours, minutes, seconds}})`.
 *
 * ```gleam
 * let time = dynamic.from("2025-03-03T16:28:13.700Z")
 * let tuple = dynamic.from(#(#(2025, 3, 3), #(16, 28, 13)))
 * let tuple_ns = dynamic.from(#(#(2025, 3, 3), #(16, 28, 13.700)))
 * decode.run(time, decrypt.timestamp())  // -> Ok(Timestamp)
 * decode.run(tuple, decrypt.timestamp()) // -> Ok(Timestamp)
 * decode.run(tuple_ns, decrypt.timestamp()) // -> Ok(Timestamp)
 * ```
 */
export function timestamp() {
  let tuple = tuple_timestamp();
  let rfc3339 = rfc3339_decoder();
  let timestamp$1 = microseconds_decoder();
  let _pipe = $decode.one_of(tuple, toList([rfc3339, timestamp$1]));
  return $decode.collapse_errors(_pipe, "Timestamp");
}

/**
 * Decode in the same way a JSON string or a `Dynamic` data structure
 * corresponding to the JSON string already parsed. \
 * Such function can be
 * used in the same way with fields coming from Postgres (JSON string
 * formatted) or coming from HTTP calls (dynamically formatted).
 *
 * ```gleam
 * let decoder = decrypt.json(decode.int)
 * let data = json.to_string(json.int(1))
 * json.parse(data, decoder) // -> Ok(1)
 * decode.run(data, decoder) // -> Ok(1)
 * let data = dynamic.from(1)
 * decode.run(data, decoder) // -> Ok(1)
 * ```
 */
export function json(decoder) {
  let _pipe = (_capture) => { return decode_json(decoder, _capture); };
  return new_decoder(_pipe);
}

/**
 * Decode a UUID, whether in string format (`xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`)
 * or as a bit array. Decode it as a string format no matter the target,
 * to ensure wide-compatibility between targets.
 *
 * ```gleam
 * import gleam/dynamic
 * import gleam/dynamic/decode
 * import youid/uuid
 *
 * let bytes_uuid = uuid.v4() |> uuid.to_bit_array |> dynamic.from
 * let string_uuid = uuid.v4() |> uuid.to_string |> dynamic.from
 * let x = decode.run(bytes_uuid, decrypt.uuid())
 * let y = decode.run(string_uuid, decrypt.uuid())
 * // x : Result(String, decode.DecodeError)
 * // y : Result(String, decode.DecodeError)
 * ```
 */
export function uuid() {
  let _pipe = $decode.one_of(
    $decode.string,
    toList([
      $decode.then$(
        $decode.bit_array,
        (content) => {
          let $ = $uuid.from_bit_array(content);
          if ($ instanceof Ok) {
            let uuid$1 = $[0];
            return $decode.success($uuid.to_string(uuid$1));
          } else {
            return $decode.failure("", "UUID");
          }
        },
      ),
    ]),
  );
  let _pipe$1 = $decode.map(_pipe, $string.lowercase);
  return $decode.collapse_errors(_pipe$1, "UUID");
}

/**
 * Decode a string field as the exact same string provided in parameter.
 * Fail otherwise.
 *
 * ```gleam
 * // Fail if type is different than "document".
 * use _ <- decode.field("type", decrypt.type_("document"))
 * // Continue decoding knowing the data is a document.
 * use content <- decode.field("field", decode.string)
 * decode.success(content)
 * ```
 */
export function type_(type_) {
  return $decode.then$(
    $decode.string,
    (content) => {
      let content$1 = content;
      if (content$1 === type_) {
        return $decode.success(content$1);
      } else {
        return $decode.failure(type_, "Type");
      }
    },
  );
}

/**
 * Decode a string field as the exact same string provided in parameter if the
 * field exists. If it does not exists, skip the test and continue.
 * Fail otherwise.
 *
 * ```gleam
 * // Fail if type exists & is different than "document". If it does not exist,
 * // skip the check and continue with the type.
 * use _ <- decode.field("type", decrypt.optional_type_("document"))
 * // Continue decoding knowing the data is a document.
 * use content <- decode.field("field", decode.string)
 * decode.success(content)
 * ```
 */
export function optional_type_(type_) {
  return $decode.then$(
    $decode.optional($decode.string),
    (content) => {
      if (content instanceof $option.Some) {
        let content$1 = content[0];
        if (content$1 === type_) {
          return $decode.success(content$1);
        } else {
          return $decode.failure(type_, "Type");
        }
      } else {
        return $decode.success(type_);
      }
    },
  );
}

/**
 * Decode a totally optional field as `option.Option(x)`. Default value is
 * `option.None`. You can always swap `decode.field("x", decode.optional(decoder))`
 * with `decrypt.optional_field("x", decoder)`, as `decrypt.optional_field`
 * is more permissive than the other form, and will automatically fallback
 * on `option.None` when the field is either missing, or the field is optional.
 *
 * ```gleam
 * use str: Option(String) <- decrypt.optional_field("field", decode.string)
 * use int: Option(Int) <- decrypt.optional_field("field", decode.int)
 * decode.success(#(str, int))
 * ```
 */
export function optional_field(field, decoder, next) {
  let decoder$1 = $decode.optional(decoder);
  return $decode.optional_field(field, new None(), decoder$1, next);
}

/**
 * Decode a first Decoder working on arbitrary data, and returning `Dynamic`
 * data, then run the decoder itself. This allows for arbitrary data mapping
 * on `Dynamic` data before decoding them.
 *
 * > Never use this function in first intention.
 */
export function preprocess(decoder_a, decoder_b) {
  return $decode.then$(
    decoder_a,
    (content) => {
      let decoder_b$1 = decoder_b();
      let _pipe = $decode.run(content, decoder_b$1);
      let _pipe$1 = $result.map(_pipe, $decode.success);
      return $result.unwrap(_pipe$1, decoder_b$1);
    },
  );
}

/**
 * Decode a list of results, and ignore the errors. Similar to `filter_map`,
 * but for decoders.
 *
 * ```gleam
 * decode.nullable(decode.string)
 * |> decode.map(option.to_result(_, Nil))
 * |> decode.filter_list
 * ```
 */
export function filter_list(decoder) {
  return $decode.map(
    $decode.list(decoder),
    (content) => { return $list.filter_map(content, $function.identity); },
  );
}
